import React, { useEffect, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { ProgressBar } from "primereact/progressbar";
import { syncKpiFromSheets } from "../../../services/syncKpiFromSheets";

interface Props {
  visible: boolean;
  onHide: () => void;
}

type Status = "idle" | "syncing" | "done" | "error";

const CLIENT_ID = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID ?? "";
const SCOPE = "https://www.googleapis.com/auth/spreadsheets.readonly";

const SyncKpiDataDialog: React.FC<Props> = ({ visible, onHide }) => {
  const [status, setStatus]     = useState<Status>("idle");
  const [progress, setProgress] = useState<string>("");
  const [error, setError]       = useState<string | null>(null);
  const tokenClientRef          = useRef<TokenClient | null>(null);

  useEffect(() => {
    if (!visible) return;
    // Initialise GIS token client once the dialog opens (ensures the script is loaded)
    const init = () => {
      if (!window.google?.accounts?.oauth2) return;
      tokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
        client_id: CLIENT_ID,
        scope: SCOPE,
        callback: async (response) => {
          if (response.error || !response.access_token) {
            setError(response.error ?? "Google authorisation failed.");
            setStatus("error");
            return;
          }
          try {
            await syncKpiFromSheets(response.access_token, (msg) => {
              setProgress(msg);
            });
            setStatus("done");
            setTimeout(() => window.location.reload(), 1200);
          } catch (err: any) {
            setError(err?.message ?? "An unexpected error occurred.");
            setStatus("error");
          }
        },
      });
    };

    if (window.google?.accounts?.oauth2) {
      init();
    } else {
      // GIS script is loaded with async — give it a moment before giving up
      const timeout = setTimeout(() => {
        if (window.google?.accounts?.oauth2) {
          init();
        } else {
          setError("Google Identity Services failed to load. Please reload the page and try again.");
          setStatus("error");
        }
      }, 1500);
      return () => clearTimeout(timeout);
    }
  }, [visible]);

  const handleSync = () => {
    if (!CLIENT_ID) {
      setError("REACT_APP_GOOGLE_OAUTH_CLIENT_ID is not configured. Add it to the .env file.");
      setStatus("error");
      return;
    }
    if (!tokenClientRef.current) {
      setError("Google Identity Services not initialized. Please reload the page.");
      setStatus("error");
      return;
    }
    setStatus("syncing");
    setError(null);
    setProgress("Starting…");
    tokenClientRef.current.requestAccessToken({ prompt: "" });
  };

  const handleClose = () => {
    setStatus("idle");
    setError(null);
    setProgress("");
    onHide();
  };

  return (
    <Dialog
      header="Sync KPI Data from Google Sheets"
      visible={visible}
      onHide={handleClose}
      style={{ width: 480 }}
      dismissableMask={status !== "syncing"}
      closable={status !== "syncing"}
    >
      <p className="text-color-secondary text-sm mt-0 mb-4">
        Fetches the latest data directly from all Google Sheets sources and
        saves it locally. A Google authorisation popup will appear.
      </p>

      {error && (
        <Message severity="error" className="mb-3 w-full" text={error} />
      )}

      {status === "syncing" && (
        <div className="mb-4">
          <p className="text-sm mb-2">{progress}</p>
          <ProgressBar mode="indeterminate" style={{ height: 6 }} />
        </div>
      )}

      {status === "done" && (
        <Message
          severity="success"
          className="mb-3 w-full"
          text="Sync complete! Reloading the page…"
        />
      )}

      {(status === "idle" || status === "error") && (
        <Button
          label="Connect with Google & Sync"
          icon="pi pi-google"
          className="w-full"
          onClick={handleSync}
        />
      )}

      {status === "syncing" && (
        <Button
          label="Syncing…"
          icon="pi pi-spin pi-spinner"
          className="w-full"
          disabled
        />
      )}
    </Dialog>
  );
};

export default SyncKpiDataDialog;
